import React, { useEffect, useState, useRef, useCallback } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { customFetch } from '../apiClient';
import CreatableSelect from 'react-select/creatable';


// ✅ DYNAMIC STYLES FOR REACT SELECT
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '38px',
    fontSize: '13px',
    borderRadius: '6px',
    borderColor: state.isFocused ? 'var(--theme-color, #0088cc)' : '#cbd5e0',
    boxShadow: state.isFocused ? '0 0 0 3px color-mix(in srgb, var(--theme-color, #0088cc), transparent 85%)' : 'none',
    '&:hover': { borderColor: 'var(--theme-color, #0088cc)' },
    borderWidth: '1.5px'
  }),
  menu: (base) => ({ ...base, zIndex: 9999, borderRadius: '6px' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    fontSize: '13px',
    padding: '8px 12px',
    backgroundColor: state.isSelected 
      ? 'var(--theme-color, #0088cc)' 
      : state.isFocused 
        ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)' 
        : 'white',
    color: state.isSelected ? 'white' : '#333',
    cursor: 'pointer',
    '&:active': { backgroundColor: 'var(--theme-color, #0088cc)', color: 'white' }
  }),
  placeholder: (base) => ({ ...base, color: '#a0aec0', fontSize: '13px' })
};

// Static Options
const unitOptions = [
  { value: "Leaf", label: "Leaf" }, { value: "Piece", label: "Piece" },
  
];

const gstOptions = [
  { value: "0", label: "0%" }, { value: "5", label: "5%" },
  { value: "12", label: "12%" }, { value: "18", label: "18%" },
  { value: "28", label: "28%" },
];

const MedicineSearchList = () => {
  const [filters, setFilters] = useState({
    medicine: "", categoryid: "", brandid: null, brandName: ""
  });

  const [categories, setCategories] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [medicines, setMedicines] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [showEditModal, setShowEditModal] = useState(false);
  const [editMedicine, setEditMedicine] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [brandOptions, setBrandOptions] = useState([]);
  

  const formRef = useRef(null);
  const editFormRef = useRef(null);
useEffect(() => {
    const loadCategories = async () => {
      try {
       const res = await customFetch(`/MedCategoryDT`);
        if (res.ok) {
            const data = await res.json();
            setCategories(data);
            setCategoryOptions(data.map(c => ({ value: c.cid, label: c.name })));
        }
      } catch (err) { console.error(err); }
    };

    // 👇 NAYA: Saare Brands Load karne ke liye
    const loadAllBrands = async () => {
      try {
        const res = await customFetch(`/MedBrandDT`); // Bina search ke bulayein
        if (res.ok) {
          const data = await res.json();
          setBrandOptions(data.map(b => ({ value: b.bid, label: b.bName })));
        }
      } catch (err) { console.error(err); }
    };

    loadCategories();
    loadAllBrands(); // 👈 Call karein
}, []);

  const loadBrandOptions = async (inputValue) => {
    if (!inputValue || !inputValue.trim()) return [];
    try {
const res = await customFetch(`/MedBrandDT?search=${encodeURIComponent(inputValue.trim())}`);
      if (!res.ok) return [];
      const data = await res.json();
      return data.map((b) => ({ value: b.bid, label: b.bName }));
    } catch (err) { return []; }
  };

  const handleFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSelectChange = (name, selected) => {
      setFilters(prev => ({ ...prev, [name]: selected ? selected.value : "" }));
  };

  const handleBrandChange = (selected) => {
    setFilters((prev) => ({
      ...prev, brandid: selected ? selected.value : null, brandName: selected ? selected.label : ""
    }));
  };

  const handleSearch = async (e) => {
    e.preventDefault(); setError(""); setSuccess(""); setLoading(true);
    try {
    
      const params = new URLSearchParams();
      if (filters.medicine?.trim()) params.append("medicine", filters.medicine.trim());
      if (filters.categoryid) params.append("categoryId", filters.categoryid);
      if (filters.brandid) params.append("brandId", filters.brandid);

   const res = await customFetch(`/MedicineDT/search?${params.toString()}`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setMedicines(data); setSuccess(`Found ${data.length} record(s).`);
    } catch (err) { setError(err.message); setMedicines([]); } finally { setLoading(false); }
  };

  const formatQuantity = (qtyInPc, unit, packSize) => {
    const pc = parseFloat(qtyInPc || 0);
    const pack = parseFloat(packSize || 1);

    // Agar unit Leaf/Strip/Box hai toh pieces ko packSize se divide karo
    if (["Leaf", "Strip", "Box"].includes(unit) && pack > 0) {
        return (pc / pack).toFixed(2); // Example: 10 pieces / 10 pack = 1.00 Leaf
    }
    
    // Agar unit "Piece" hai toh direct return karo
    return pc.toFixed(2);
};


  const handleReset = () => {
    setFilters({ medicine: "", categoryid: "", brandid: null, brandName: "" });
    setMedicines([]); setError(""); setSuccess("");
  };

// ✅ UNIFIED KEYBOARD NAVIGATION (Search Form + Edit Modal)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      // Dropdown open hone par enter skip karein
      if (e.target.closest('.react-select__menu')) return;

      // Smart Logic: Check karein Modal open hai ya nahi
      const activeRef = showEditModal ? editFormRef : formRef;
      
      // Agar active ref nahi mila ya user bahar click karke enter daba raha hai, toh ignore karein
      if (!activeRef.current || !activeRef.current.contains(e.target)) return;

      const focusableSelector = 
        'input:not([disabled]):not([type="hidden"]):not([readonly]), ' +
        'select:not([disabled]), ' +
        'textarea:not([disabled]):not([readonly]), ' +
        'button:not([disabled])';

      const focusableElements = Array.from(
        activeRef.current.querySelectorAll(focusableSelector) || []
      );

      if (focusableElements.length === 0) return;
      const currentIndex = focusableElements.indexOf(document.activeElement);

      // Agar last element par Enter dabaya (yani Submit / Update button par)
      if (currentIndex === focusableElements.length - 1) {
        e.preventDefault();
        if (showEditModal) {
          saveEdit(); // Modal me Update function call karein
        } else {
          activeRef.current.requestSubmit(); // Main page par Search form submit karein
        }
        return;
      }

      // Next element par focus karein
      if (currentIndex >= 0) {
        e.preventDefault();
        focusableElements[currentIndex + 1]?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showEditModal]); // 👈 showEditModal dependency zaroori hai

  // ✅ MODAL OPEN HOTE HI PEHLE FIELD PAR FOCUS
  useEffect(() => {
    if (showEditModal && editFormRef.current) {
      setTimeout(() => {
        const firstInput = editFormRef.current.querySelector('input:not([disabled]):not([type="hidden"])');
        firstInput?.focus();
      }, 100);
    }
  }, [showEditModal]);

  // // ✅ 2. ATTACH LISTENER & SET DEFAULT FOCUS
  // useEffect(() => {
  //   document.addEventListener("keydown", handleKeyDown);

  //   // पेज लोड होते ही पहले इनपुट (Medicine Name) पर फोकस करें
  //   if (formRef.current && !showEditModal) {
  //     const firstInput = formRef.current.querySelector('input:not([disabled]):not([type="hidden"])');
  //     firstInput?.focus();
  //   }

  //   return () => document.removeEventListener("keydown", handleKeyDown);
  // }, [handleKeyDown, showEditModal]);

  const openEditModal = async (medicine) => {
    let brandName = medicine.brandName || "";
    if (medicine.brandid && !brandName) {
      try {
       const res = await customFetch(`/MedBrandDT?search=${medicine.brandid}`);
        if (res.ok) {
          const brands = await res.json();
          const brand = brands.find(b => b.bid == medicine.brandid);
          brandName = brand ? brand.bName : `ID: ${medicine.brandid}`;
        }
      } catch (err) { brandName = `ID: ${medicine.brandid}`; }
    }

    const displayOpQty = (["Leaf", "Box", "Strip"].includes(medicine.mUnit) && medicine.iUnit > 0)
        ? (parseFloat(medicine.opqty) / parseFloat(medicine.iUnit)).toString()
        : medicine.opqty?.toString() || "";

    setEditMedicine({
      ...medicine,
      brandName: brandName,
      opqty: displayOpQty,
      gstp: medicine.gstp?.toString() || "",
      mUnit: medicine.mUnit || "",
      iUnit: medicine.iUnit?.toString() || "",
      sDis: medicine.sDis?.toString() || "",
      sRate: medicine.sRate?.toString() || "",
      mrp: medicine.mrp?.toString() || ""     
    });
    setEditError(""); setShowEditModal(true);
  };

  const closeEditModal = () => { setShowEditModal(false); setEditMedicine(null); setEditError(""); };

  const calculateEditFields = (name, value) => {
    const currentMrp = parseFloat(editMedicine?.mrp || value || 0);
    const currentSdis = parseFloat(editMedicine?.sDis || 0);
    
    if (name === "mrp") {
      if (currentSdis > 0) {
        const discountAmount = (parseFloat(value) * currentSdis) / 100;
        setEditMedicine(prev => ({ ...prev, sRate: (parseFloat(value) - discountAmount).toFixed(2), mrp: value }));
      } else {
        setEditMedicine(prev => ({ ...prev, mrp: value }));
      }
    } else if (name === "sDis") {
      if (currentMrp > 0) {
        const discountAmount = (currentMrp * parseFloat(value)) / 100;
        setEditMedicine(prev => ({ ...prev, sRate: (currentMrp - discountAmount).toFixed(2), sDis: value }));
      } else {
        setEditMedicine(prev => ({ ...prev, sDis: value }));
      }
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    if (['mrp', 'sDis'].includes(name)) calculateEditFields(name, value);
    else setEditMedicine((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSelectChange = (name, selectedOption) => {
      setEditMedicine(prev => ({ ...prev, [name]: selectedOption ? selectedOption.value : "" }));
  };

  const handleEditBrandChange = (selected) => {
    setEditMedicine(prev => ({ ...prev, brandid: selected ? selected.value : null, brandName: selected ? selected.label : "" }));
  };

  const saveEdit = async () => {
    if (!editMedicine) return;
    if (!editMedicine.medicine?.trim()) { setEditError("Medicine name is required."); return; }

    setEditLoading(true); setEditError("");
    try {
      const token = sessionStorage.getItem("token");
      
      // ============================================================
      // 🚀 CALCULATE TOTAL PIECES (Logic Applied Correctly)
      // ============================================================
      const opQty = parseFloat(editMedicine.opqty) || 0;
      const packSize = parseFloat(editMedicine.iUnit) || 1;
      const unit = editMedicine.mUnit || "";

      let totalPieces = 0;

      if (["Leaf", "Box", "Strip"].includes(unit)) {
          totalPieces = opQty * packSize;
      } else {
          totalPieces = opQty; 
      }
      
      

      const fd = new FormData();
      fd.append("Medicine", editMedicine.medicine.trim());
      fd.append("Brandid", editMedicine.brandid);
      fd.append("Categoryid", editMedicine.categoryid);
      
      // ✅ CHANGE IS HERE: Send 'totalPieces' instead of 'editMedicine.opqty'
      fd.append("OPQTY", totalPieces); 

      if (editMedicine.gstp) fd.append("GSTP", editMedicine.gstp);
      if (editMedicine.mUnit) fd.append("MUnit", editMedicine.mUnit.trim());
      if (editMedicine.iUnit) fd.append("IUnit", editMedicine.iUnit);
      if (editMedicine.mediInUnit) fd.append("MediInUnit", editMedicine.mediInUnit.trim());
    fd.append("SDis", editMedicine.sDis || 0);
fd.append("SRate", editMedicine.sRate || 0);
fd.append("MRP", editMedicine.mrp || 0);
      if (editMedicine.hsncode) fd.append("HSNCODE", editMedicine.hsncode);

       const res = await customFetch(`/MedicineDT/${editMedicine.medID}`, {
  method: "PUT",
  body: fd
});
     if (!res.ok) throw new Error(await res.text());


    setMedicines((prev) => 
    prev.map((m) => 
        m.medID === editMedicine.medID 
            ? { ...m, ...editMedicine, opqty: totalPieces } // totalPieces wahi hai jo humne pieces mein calculate kiya tha
            : m
    )
);
     setSuccess("Medicine updated successfully."); closeEditModal();
   } catch (err) { setEditError(err.message); } finally { setEditLoading(false); }
 };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete medicine "${row.medicine}"?`)) return;
    try {
   const res = await customFetch(`/MedicineDT/${row.medID}`, {
  method: "DELETE"
});
      if (!res.ok) throw new Error(await res.text());
      setMedicines((prev) => prev.filter((m) => m.medID !== row.medID)); setSuccess("Medicine deleted successfully.");
    } catch (err) { setError(err.message); }
  };

  return (
    <div className="search-list-container">
      <div className="search-card">
        <div className="card-header">
          <div className="header-title"><i className="bi bi-search"></i> Search Medicines</div>
          <div className="header-actions">
   
</div>
        </div>
        <div className="card-body">
          {error && <div className="alert-custom alert-danger">{error}</div>}
          {success && <div className="alert-custom alert-success">{success}</div>}

          <form ref={formRef} onSubmit={handleSearch}>
            <div className="form-row three-col">
              <div className="form-group">
                <label className="form-label">Medicine Name</label>
                <input name="medicine" value={filters.medicine} onChange={handleFilterChange} className="form-input" placeholder="Search Name..." />
              </div>
              
              {/* Category Select */}
              <div className="form-group">
                <label className="form-label">Category</label>
                <Select 
                    options={categoryOptions} 
                    value={categoryOptions.find(c => c.value === filters.categoryid) || null}
                    onChange={(opt) => handleSelectChange('categoryid', opt)}
                    placeholder="All Categories" isClearable styles={selectStyles}
                    menuPortalTarget={document.body} menuPosition="fixed"
                />
              </div>

              {/* Brand Select */}
              <div className="form-group">
                <label className="form-label">Brand</label>
              <Select 
                options={brandOptions} 
                value={brandOptions.find(b => b.value === filters.brandid) || null}
                onChange={handleBrandChange} 
                placeholder="Search Brand..." 
                isClearable 
                isSearchable={true}
                styles={selectStyles}
                menuPortalTarget={document.body} 
                menuPosition="fixed"
            />
              </div>
            </div>

            <div className="form-footer">
              <button type="submit" className="btn-search" disabled={loading}>{loading ? "Searching..." : "Search"}</button>
              <button type="button" className="btn-reset" onClick={handleReset}>Reset</button>
            </div>
          </form>

          {/* Table */}
          <div className="table-container mt-4">
            <table className="custom-table">
              <thead><tr><th>Sr</th><th>Medicine</th><th>Category</th><th>Brand</th><th>GST%</th><th>Pack</th><th>Unit</th><th>Stock</th><th>MRP</th><th>Sale Rate</th><th>Actions</th></tr></thead>
              <tbody>
                {medicines.length === 0 ? <tr><td colSpan="12" className="text-center p-3">No medicines found.</td></tr> : medicines.map((m, i) => {
    const opening = parseFloat(m.opqty || 0);
    const purchase = parseFloat(m.toPurQtyInPc || 0);
    const sale = parseFloat(m.toSalQtyInPc || 0);
    const expired = parseFloat(m.expQTY || 0);
    const damaged = parseFloat(m.dmgQTY || 0);
    const debit = parseFloat(m.debitQTY || 0);

    // ✅ Formula: (Op + Pur) - (Sale + Exp + Dmg + Debit)
    const balanceInPc = (opening + purchase) - (sale + expired + damaged + debit);
                    return (
                        <tr key={m.medID}>
                            <td>{i+1}</td><td>{m.medicine}</td><td>{m.categoryName}</td><td>{m.brandName}</td><td>{m.gstp}%</td><td>{m.iUnit}</td><td>{m.mUnit}</td> <td style={{color: balanceInPc > 0 ? 'green' : 'red', fontWeight: 'bold'}}>
                                {formatQuantity(balanceInPc, m.mUnit, m.iUnit)}
                            </td><td>{m.mrp}</td><td>{m.sRate}</td>
                           
                            <td>
                                <div className="action-buttons">
                                    <button className="btn-icon edit" onClick={() => openEditModal(m)}><i className="bi bi-pencil-square"></i></button>
                                    <button className="btn-icon delete" onClick={() => handleDelete(m)}><i className="bi bi-trash"></i></button>
                                </div>
                            </td>
                        </tr>
                    )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && editMedicine && (
        <>
          <div className="modal-overlay" onClick={closeEditModal}></div>
          <div className="modal-container">
            <div className="modal-card" ref={editFormRef}>
              <div className="modal-header"><h5>Edit Medicine</h5><button className="close-btn" onClick={closeEditModal}>&times;</button></div>
              <div className="modal-body">
                {editError && <div className="alert-custom alert-danger mb-2">{editError}</div>}
                
                <div className="form-row two-col">
                    <div className="form-group"><label>Name *</label><input name="medicine" value={editMedicine.medicine} onChange={handleEditChange} className="form-input" /></div>
                    
                    {/* Edit Category Select */}
                    <div className="form-group"><label>Category *</label>
                        <Select options={categoryOptions} value={categoryOptions.find(c => c.value === editMedicine.categoryid)} onChange={(opt) => handleEditSelectChange('categoryid', opt)} styles={selectStyles} menuPortalTarget={document.body} />
                    </div>
                </div>

                <div className="form-row two-col mt-2">
                    {/* Edit Brand Select */}
                    <div className="form-group"><label>Brand *</label>
                     <Select 
                      options={brandOptions} 
                      value={brandOptions.find(b => b.value === editMedicine.brandid) || null} 
                      onChange={handleEditBrandChange} 
                      placeholder="Select Brand..."
                      isClearable
                      isSearchable={true}
                      styles={selectStyles} 
                      menuPortalTarget={document.body} 
                  />
                    </div>
                    <div className="form-group"><label>Composition</label><input name="mediInUnit" value={editMedicine.mediInUnit} onChange={handleEditChange} className="form-input" /></div>
                </div>

                <div className="form-row four-col mt-2">
                    <div className="form-group"><label>Unit</label><Select options={unitOptions} value={unitOptions.find(u => u.value === editMedicine.mUnit)} onChange={(opt) => handleEditSelectChange('mUnit', opt)} styles={selectStyles} menuPortalTarget={document.body} /></div>
                    <div className="form-group"><label>Pack</label><input type="number" name="iUnit" value={editMedicine.iUnit} onChange={handleEditChange} className="form-input" /></div>
                    
                    <div className="form-group">
                      <label>GST%</label>
                      <CreatableSelect 
                        isClearable
                        options={gstOptions} 
                        // 🟢 NAYA LOGIC: Agar database se custom GST aayi hai, toh use properly display karega
                        value={
                          editMedicine.gstp 
                            ? (gstOptions.find(g => g.value === String(editMedicine.gstp)) || { value: String(editMedicine.gstp), label: `${editMedicine.gstp}%` }) 
                            : null
                        } 
                        onChange={(opt) => handleEditSelectChange('gstp', opt)} 
                        formatCreateLabel={(inputValue) => `Add custom "${inputValue}%"`}
                        styles={selectStyles} 
                        menuPortalTarget={document.body} 
                      />
                    </div>
                    
                    <div className="form-group"><label>HSN</label><input name="hsncode" value={editMedicine.hsncode} onChange={handleEditChange} className="form-input" /></div>
                </div>
                {/* <div className="form-row four-col mt-2">
                    <div className="form-group"><label>MRP</label><input type="number" name="mrp" value={editMedicine.mrp} onChange={handleEditChange} className="form-input" /></div>
                    <div className="form-group"><label>Disc%</label><input type="number" name="sDis" value={editMedicine.sDis} onChange={handleEditChange} className="form-input" /></div>
                    <div className="form-group"><label>Sale Rate</label><input type="number" name="sRate" value={editMedicine.sRate} className="form-input readonly" readOnly /></div>
                
                </div> */}

              </div>
              <div className="modal-footer">
                <button className="btn-cancel" onClick={closeEditModal}>Cancel</button>
                <button className="btn-save" onClick={saveEdit} disabled={editLoading}>{editLoading ? "Saving..." : "Update"}</button>
              </div>
            </div>
          </div>
        </>
      )}

    

      {/* ✅✅✅ DYNAMIC CSS ✅✅✅ */}
      <style jsx>{`
        .search-list-container { padding: 0; }
        .search-card { background: white; border-radius: 12px; box-shadow: 0 2px 15px rgba(0,0,0,0.05); border: 1px solid #e0e6ed; overflow: hidden; }
        .card-header { background: var(--theme-color, #0088cc); padding: 12px 16px; color: white; font-weight: 700; }
        .card-body { padding: 16px; }

        .form-row { display: grid; gap: 10px; }
        .three-col { grid-template-columns: 1fr 1fr 1fr; }
        .two-col { grid-template-columns: 1fr 1fr; }
        .four-col { grid-template-columns: 1fr 1fr 1fr 1fr; }

        .form-group { display: flex; flex-direction: column; }
        .form-label { font-size: 12px; font-weight: 600; color: #2c3e50; margin-bottom: 4px; }
        .form-input { padding: 8px; border: 1.5px solid #cbd5e0; border-radius: 6px; font-size: 13px; outline: none; }
        .form-input:focus { border-color: var(--theme-color, #0088cc); }
        .readonly { background: #f3f4f6; cursor: default; }

        .form-footer { margin-top: 14px; display: flex; gap: 8px; }
        .btn-search { padding: 8px 16px; background: var(--theme-color, #0088cc); color: white; border: none; border-radius: 6px; font-weight: 600; cursor: pointer; }
        .btn-reset { padding: 8px 16px; background: #e9ecef; color: #6c757d; border: none; border-radius: 6px; font-weight: 600; cursor: pointer; }

        .table-container { overflow-x: auto; }
        .custom-table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .custom-table th { background: var(--theme-color, #0088cc); color: white; padding: 10px; text-align: left; }
        .custom-table td { padding: 8px; border-bottom: 1px solid #e0e6ed; vertical-align: middle; }
        .action-buttons { display: flex; gap: 4px; }
        .btn-icon { width: 24px; height: 24px; border-radius: 4px; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; }
        .btn-icon.edit { background: #e7f3ff; color: #0088cc; }
        .btn-icon.delete { background: #fee2e2; color: #dc2626; }

        /* Modal */
        .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 1040; }
        .modal-container { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 1050; width: 95%; max-width: 700px; }
        .modal-card { background: white; border-radius: 10px; overflow: hidden; box-shadow: 0 10px 30px rgba(0,0,0,0.2); }
        .modal-header { background: var(--theme-color, #0088cc); padding: 12px 16px; display: flex; justify-content: space-between; color: white; font-weight: 700; }
        .close-btn { background: transparent; border: none; color: white; font-size: 20px; cursor: pointer; }
        .modal-body { padding: 16px; max-height: 70vh; overflow-y: auto; }
        .modal-footer { padding: 12px 16px; background: #f8f9fa; display: flex; justify-content: flex-end; gap: 8px; }
        .btn-save { padding: 8px 16px; background: var(--theme-color, #0088cc); color: white; border: none; border-radius: 6px; cursor: pointer; }
        .btn-cancel { padding: 8px 16px; background: #e9ecef; color: #6c757d; border: none; border-radius: 6px; cursor: pointer; }

        .alert-custom { padding: 8px; border-radius: 6px; font-size: 12px; margin-bottom: 10px; }
        .alert-danger { background: #fee2e2; color: #b91c1c; }
        .alert-success { background: #dcfce7; color: #15803d; }

        @media (max-width: 768px) {
            .three-col, .four-col, .two-col { grid-template-columns: 1fr; }
            .btn-search { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default MedicineSearchList;